import type React from "react";
import { useRef, useEffect, useCallback, useMemo } from "react";

export interface UseLongPressOptions {
  /**
   * 長押しと判定するまでの時間（ミリ秒）
   * @default 1000
   */
  duration?: number;
}

export interface UseLongPressHandlers {
  onPointerDown: () => void;
  onPointerUp: () => void;
  onPointerLeave: () => void;
  onPointerCancel: () => void;
  onContextMenu: (e: React.MouseEvent | Event) => void;
}

/**
 * 長押しを検出するカスタムフック
 *
 * @param onLongPress 長押し時のコールバック
 * @param options オプション（duration）
 * @returns イベントハンドラー群
 *
 * @example
 * ```tsx
 * const { handlers } = useLongPress(() => {
 *   console.log("Long pressed!");
 * }, { duration: 1000 });
 *
 * <div {...handlers}>Long press me</div>
 * ```
 */
export const useLongPress = (
  onLongPress: () => void,
  options: UseLongPressOptions = {}
) => {
  const { duration = 1000 } = options;
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackRef = useRef(onLongPress);
  // PRODUCT_DEV-583: 「離した瞬間に長押しが解除される」不具合対策。
  // firedRef: 閾値到達で長押しが発火済みかをラッチする。発火後は pointerup/leave/cancel
  //           が来ても取り消さない。
  // startRef: 押下時刻。pointerup がタイマー発火とレースした場合でも、経過時間が閾値を
  //           満たしていれば pointerup 側で発火させ、取りこぼしを防ぐ。
  const firedRef = useRef(false);
  const startRef = useRef(0);

  // Update ref when callback changes
  useEffect(() => {
    callbackRef.current = onLongPress;
  }, [onLongPress]);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const fire = useCallback(() => {
    firedRef.current = true;
    clearTimer();
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
    callbackRef.current();
  }, [clearTimer]);

  // pointerup 用: 閾値到達済みなら（タイマー発火前でも）ここで発火し、未到達なら取り消す。
  const settleOnRelease = useCallback(() => {
    if (firedRef.current) return; // 既に発火済み: 解放で取り消さない
    if (Date.now() - startRef.current >= duration) {
      fire();
    } else {
      clearTimer();
    }
  }, [duration, fire, clearTimer]);

  // pointerleave/cancel 用: 未発火なら取り消す。発火済みなら何もしない（取り消さない）。
  const cancelIfPending = useCallback(() => {
    if (!firedRef.current) {
      clearTimer();
    }
  }, [clearTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, [clearTimer]);

  const handlers: UseLongPressHandlers = useMemo(
    () => ({
      onPointerDown: () => {
        clearTimer();
        firedRef.current = false;
        startRef.current = Date.now();
        timeoutRef.current = setTimeout(() => {
          fire();
        }, duration);
      },
      onPointerUp: settleOnRelease,
      onPointerLeave: cancelIfPending,
      onPointerCancel: cancelIfPending,
      onContextMenu: (e: React.MouseEvent | Event) => {
        e.preventDefault();
      },
    }),
    [duration, clearTimer, fire, settleOnRelease, cancelIfPending]
  );

  return { handlers };
};
